import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { X, MapPin, Timer } from 'lucide-react';
import { api } from '../../lib/apiClient';
import type { Checkin } from '../../lib/types';
import { Button } from '../ui/Button';

/**
 * EndCheckinSheet — "I'm out" → optionally log the swim. Mirrors
 * iOS EndCheckinSheet: pre-filled summary + temp + notes, two
 * primary actions (Log + finish, Just finish without logging).
 *
 * The actual duration is capped at the user's intended duration
 * + 15 min slack so a forgotten check-in doesn't log a 4h swim.
 */
export function EndCheckinSheet({
    open,
    checkin,
    onDone,
    onCancel,
}: {
    open: boolean;
    checkin: Checkin;
    onDone: () => void;
    onCancel: () => void;
}) {
    const qc = useQueryClient();
    const [tempStr, setTempStr] = useState('');
    const [notes, setNotes] = useState('');
    const [busy, setBusy] = useState(false);

    if (!open) return null;

    const expectedMin = Math.round((new Date(checkin.expectedEndAt).getTime() - new Date(checkin.createdAt).getTime()) / 60_000);
    const elapsedMin = Math.max(1, Math.floor((Date.now() - new Date(checkin.createdAt).getTime()) / 60_000));
    const actualMin = Math.min(elapsedMin, expectedMin + 15);

    async function logAndFinish() {
        setBusy(true);
        try {
            const tempNum = tempStr ? Number(tempStr.replace(',', '.')) : undefined;
            await api.post('/v1/me/swims', {
                id: crypto.randomUUID(),
                startedAt: checkin.createdAt,
                durationMin: actualMin,
                ...(tempNum != null && !Number.isNaN(tempNum) ? { temperatureC: tempNum } : {}),
                ...(checkin.spotId ? { spotId: checkin.spotId } : {}),
                lat: checkin.lat,
                lon: checkin.lon,
                ...(notes.trim() ? { notes: notes.trim() } : {}),
            });
            await api.post(`/v1/me/checkins/${checkin.id}/end`, {});
            qc.invalidateQueries({ queryKey: ['me'] });
            onDone();
        } catch (err) {
            console.warn('[end-checkin] failed', err);
            window.alert("Hattie couldn't save. Try again in a moment.");
        } finally {
            setBusy(false);
        }
    }

    async function justFinish() {
        setBusy(true);
        try {
            await api.post(`/v1/me/checkins/${checkin.id}/end`, {});
            qc.invalidateQueries({ queryKey: ['me'] });
            onDone();
        } finally {
            setBusy(false);
        }
    }

    return (
        // pointer-events-auto for iOS Safari — see CheckinStartSheet
        // for the diagnosis. EndCheckinSheet can launch from the
        // active-checkin banner globally, which may render inside
        // the map overlay tree with pointer-events: none.
        <div className="fixed inset-0 z-50 pointer-events-auto flex items-end md:items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl flex flex-col max-h-[90vh]">
                <div className="bg-gradient-to-br from-verdict-good to-hattie-teal text-white px-6 pt-6 pb-4 relative shrink-0">
                    {/* X = "I'm done, just don't log it" — fires
                      justFinish so the check-in ends cleanly. Pre-fix,
                      X called onCancel which only DISMISSED the dialog
                      while leaving the timer running — the user tapped
                      "I'm out", saw the log prompt, tapped X expecting
                      "end without logging", and got dropped back into
                      the running timer. Confusing. Aligned with the
                      secondary text button below — two affordances,
                      same outcome. Fixed May 22 2026.
                      onCancel kept available on the parent contract
                      in case any future caller wants the "keep timer
                      running" escape hatch; not exposed here. */}
                    <button onClick={justFinish} disabled={busy} className="absolute top-3 right-3 w-11 h-11 rounded-full bg-white/20 hover:bg-white/30 active:bg-white/40 disabled:opacity-50 flex items-center justify-center" aria-label="Finish without logging">
                        <X className="w-4 h-4" />
                    </button>
                    <p className="text-xs uppercase tracking-[0.2em] opacity-90 font-bold">You're out</p>
                    <h2 className="font-display text-3xl tracking-tight mt-1">Log this one?</h2>
                </div>
                <div className="p-6 space-y-5 overflow-y-auto">
                    <div className="space-y-2 text-sm">
                        <div className="flex items-center justify-between">
                            <span className="flex items-center gap-2 text-hattie-mist">
                                <MapPin className="w-4 h-4" /> Spot
                            </span>
                            <span className="font-semibold text-hattie-ink">{checkin.spotId ? 'Saved spot' : 'Ad-hoc'}</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="flex items-center gap-2 text-hattie-mist">
                                <Timer className="w-4 h-4" /> Time in
                            </span>
                            <span className="font-display text-xl text-hattie-ink">{actualMin} min</span>
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs uppercase tracking-wider text-hattie-mist font-bold mb-2">
                            Water temp (°C, optional)
                        </label>
                        <input
                            type="number"
                            step="0.5"
                            min={-2}
                            max={30}
                            value={tempStr}
                            onChange={(e) => setTempStr(e.target.value)}
                            placeholder="e.g. 11.5"
                            className="w-full p-3 border border-hattie-stone rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-hattie-teal/40"
                        />
                        <p className="text-xs text-hattie-mist mt-1">If you brought a thermometer. Otherwise leave blank.</p>
                    </div>

                    <div>
                        <label className="block text-xs uppercase tracking-wider text-hattie-mist font-bold mb-2">
                            Notes
                        </label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value.slice(0, 1000))}
                            rows={3}
                            placeholder="How was it?"
                            className="w-full p-3 border border-hattie-stone rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-hattie-teal/40 resize-none"
                        />
                    </div>
                </div>
                <div className="p-4 border-t border-hattie-stone shrink-0 bg-hattie-paper space-y-2">
                    <Button onClick={logAndFinish} disabled={busy} className="w-full" size="lg">
                        {busy ? 'Saving…' : 'Log + finish'}
                    </Button>
                    <button
                        onClick={justFinish}
                        disabled={busy}
                        className="w-full text-xs text-hattie-mist hover:text-hattie-ink py-2"
                    >
                        Just finish — don't log
                    </button>
                </div>
            </div>
        </div>
    );
}
